<template>
  <b-modal
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :title="fileName ? `QR Code \u2014 ${fileName}` : 'QR Code'"
    size="sm"
    ok-only
    ok-title="Close">

    <!-- QR code available -->
    <div class="text-center" v-if="qrCodeUrl">
      <!-- Display: PNG (sharp at ~300px, good for modal web display) -->
      <img
        :src="qrCodeUrl"
        class="img-fluid qr-code-image"
        :alt="fileName ? `QR Code for ${fileName}` : 'QR Code'" />

      <p class="text-muted mt-2 small">
        Scan this code with your phone to quickly access this {{ fileTypeLower }} &mdash; no login required.
      </p>

      <!-- Download button — prefers SVG (resolution-independent, ideal for print) -->
      <div class="mt-2">
        <a v-if="qrCodeSvgUrl"
           :href="qrCodeSvgUrl"
           :download="`qrcode-${fileName || 'file'}.svg`"
           class="btn btn-outline-secondary btn-sm">
          <i class="fas fa-download mr-1"></i> Download QR Code (SVG)
        </a>
        <a v-else-if="qrCodeUrl"
           :href="qrCodeUrl"
           :download="`qrcode-${fileName || 'file'}.png`"
           class="btn btn-outline-secondary btn-sm">
          <i class="fas fa-download mr-1"></i> Download QR Code
        </a>
      </div>
    </div>

    <!-- QR code not yet available -->
    <div v-else class="text-center text-muted py-4">
      <i class="fas fa-qrcode fa-3x mb-3"></i>
      <p>QR code is not yet available for this file.</p>
    </div>

    <!-- Share link bar (shown when publicShareUrl is present) -->
    <div class="mt-3" v-if="publicShareUrl">
      <label class="small font-weight-bold">Share Link</label>

      <!--
        Mobile: navigator.share() surfaces the native OS share sheet
        (SMS, WhatsApp, Email, AirDrop) — far better UX than copy-paste on a phone.
        Desktop: clipboard copy pattern with input + copy button.
      -->
      <div v-if="canNativeShare && nativeShareEnabled" class="d-flex">
        <div class="input-group input-group-sm flex-grow-1 mr-2">
          <input type="text" class="form-control" :value="publicShareUrl" readonly />
        </div>
        <button class="btn btn-primary btn-sm" type="button" @click="shareFile">
          <i class="fas fa-share-alt mr-1"></i> Share
        </button>
      </div>

      <div v-else class="input-group input-group-sm">
        <input
          type="text"
          class="form-control"
          :value="publicShareUrl"
          readonly
          ref="shareLinkInput" />
        <div class="input-group-append">
          <button class="btn btn-outline-primary" type="button" @click="copyShareLink">
            <i class="fas fa-copy mr-1"></i>{{ copyButtonLabel }}
          </button>
        </div>
      </div>

      <small class="text-muted">Anyone with this link can view the file without logging in.</small>
    </div>
  </b-modal>
</template>

<script>
/**
 * QRCodeModal.vue
 *
 * Displays the QR code for a file. Accessible from the file action dropdown menu.
 * Includes a download button (SVG preferred, PNG fallback) and a share link.
 *
 * Props:
 *   visible        {Boolean} — controls visibility (.sync / update:visible)
 *   qrCodeUrl      {String}  — qrCodePublicUrl from API (PNG ~300px, for web display)
 *   qrCodeSvgUrl   {String}  — qrCodeSvgPublicUrl from API (SVG, for download/print)
 *   publicShareUrl {String}  — publicShareUrl from API (URL encoded in the QR code)
 *   fileName       {String}  — display name of the file
 *   fileType       {String}  — 'Document' | 'Image' | 'Video'
 */
export default {
  name: 'QRCodeModal',

  props: {
    visible: { type: Boolean, default: false },
    /** qrCodePublicUrl from API — PNG for web display */
    qrCodeUrl: { type: String, default: null },
    /** qrCodeSvgPublicUrl from API — SVG for download */
    qrCodeSvgUrl: { type: String, default: null },
    /** publicShareUrl from API — the URL encoded in the QR code */
    publicShareUrl: { type: String, default: null },
    fileName: { type: String, default: '' },
    /** 'Document' | 'Image' | 'Video' */
    fileType: { type: String, default: 'Document' }
  },

  data () {
    return {
      copyButtonLabel: 'Copy Link',
      /**
       * navigator.share is available on iOS Safari 12.1+, Android Chrome, and
       * most modern mobile browsers. Check at runtime — desktop support varies.
       */
      canNativeShare: typeof navigator !== 'undefined' && typeof navigator.share === 'function',
      // Set to true to re-enable the OS native share sheet (SMS, social media, etc.)
      nativeShareEnabled: false
    }
  },

  computed: {
    fileTypeLower () {
      return this.fileType ? this.fileType.toLowerCase() : 'file'
    }
  },

  methods: {
    copyShareLink () {
      return navigator.clipboard.writeText(this.publicShareUrl)
        .then(() => {
          this.copyButtonLabel = 'Link Copied!'
          setTimeout(() => { this.copyButtonLabel = 'Copy Link' }, 2000)
        })
        .catch(() => {
          // Fallback for iOS Safari strict clipboard security — select the input
          if (this.$refs.shareLinkInput) {
            this.$refs.shareLinkInput.select()
            document.execCommand('copy')
            this.copyButtonLabel = 'Link Copied!'
            setTimeout(() => { this.copyButtonLabel = 'Copy Link' }, 2000)
          }
        })
    },

    shareFile () {
      if (navigator.share) {
        navigator.share({
          title: `${this.fileName} \u2014 Response Grid`,
          text: `View this ${this.fileTypeLower} \u2014 no login required.`,
          url: this.publicShareUrl
        }).catch(() => {
          // User cancelled share sheet — no action needed
        })
      }
    }
  }
}
</script>

<style scoped>
.qr-code-image {
  max-width: 280px;
}
</style>



// WEBPACK FOOTER //
// src/components/QRCodeModal.vue